import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsLabels']
const EMPTY_ARRAY = []

export const useSettingsLabels = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/labels`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the labels")
      }
      return res.json()
    },
  })

  return { labels: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useAddLabel = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: addLabel, ...rest } = useMutation({
    mutationFn: async labelData => {
      const res = await fetch(`${baseURL}/v2/rest/settings/labels`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ label: labelData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not create label')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { addLabel, ...rest }
}

export const useUpdateLabel = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateLabel, ...rest } = useMutation({
    mutationFn: async ({ id, ...labelData }) => {
      const res = await fetch(`${baseURL}/v2/rest/settings/labels/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ label: labelData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update label')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateLabel, ...rest }
}

export const useDeleteLabel = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteLabel, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(`${baseURL}/v2/rest/settings/labels/${id}`, {
        method: 'DELETE',
        headers,
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete label')
      }
      return res.status === 204 ? {} : res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteLabel, ...rest }
}
